import {
  format,
  fromUnixTime,
  isSameYear,
  isToday,
  isTomorrow,
  isYesterday,
} from "date-fns";

/**
 * The one way the product writes dates.
 *
 * - formatDate:     "4 Nov" this year, "4 Nov 2025" otherwise
 * - formatDateTime: "4 Nov, 11:43" / "4 Nov 2025, 11:43"
 * - formatRelative: "Today 11:43", "Tomorrow 09:00", "Yesterday 16:20",
 *                   otherwise the same as formatDateTime
 *
 * Numbers are unix seconds, which is what the API sends.
 */
export type DateInput = Date | number;

export const toDate = (value: DateInput): Date =>
  typeof value === "number" ? fromUnixTime(value) : value;

export function formatDate(value: DateInput, now = new Date()): string {
  const d = toDate(value);
  return format(d, isSameYear(d, now) ? "d MMM" : "d MMM yyyy");
}

export function formatDateTime(value: DateInput, now = new Date()): string {
  const d = toDate(value);
  return `${formatDate(d, now)}, ${format(d, "HH:mm")}`;
}

export function formatRelative(value: DateInput, now = new Date()): string {
  const d = toDate(value);
  const time = format(d, "HH:mm");
  if (isToday(d)) {
    return `Today ${time}`;
  }
  if (isTomorrow(d)) {
    return `Tomorrow ${time}`;
  }
  if (isYesterday(d)) {
    return `Yesterday ${time}`;
  }
  return formatDateTime(d, now);
}

/** The full date, for tooltips: "Wednesday 4 November 2026, 11:43". */
export function formatFull(value: DateInput, withTime = true): string {
  return format(
    toDate(value),
    withTime ? "EEEE d MMMM yyyy, HH:mm" : "EEEE d MMMM yyyy",
  );
}
